import React, { useState, useEffect, useCallback } from 'react';
import { QrCode, Shield, RefreshCw } from 'lucide-react';
import { API } from '@/utils/api';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const TimeclockQRDisplay = () => {
  const [token, setToken] = useState('');
  const [ttl, setTtl] = useState(30);
  const [countdown, setCountdown] = useState(30);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [blocked, setBlocked] = useState(false);
  const [blockedIp, setBlockedIp] = useState('');

  const fetchToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/timeclock/qr-token`);
      setToken(res.data.token);
      setTtl(res.data.ttl);
      setCountdown(res.data.ttl);
      setBlocked(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setBlocked(true);
        setBlockedIp(err.response?.data?.detail || '');
      }
    }
  }, []);

  useEffect(() => {
    fetchToken();
    const interval = setInterval(fetchToken, 30000);
    return () => clearInterval(interval);
  }, [fetchToken]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      setCountdown(prev => {
        if (prev <= 1) { fetchToken(); return 30; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchToken]);

  const qrValue = token ? `VORTEKIO-PUNCH:${token}` : '';
  const progressPct = (countdown / 30) * 100;

  if (blocked) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6" data-testid="qr-display-blocked">
        <div className="text-center space-y-4 max-w-md">
          <Shield className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className="text-2xl font-bold text-red-400">Acces refuse</h2>
          <p className="text-slate-400 text-sm">{blockedIp}</p>
          <p className="text-slate-500 text-xs">Le QR code de timbrage est uniquement accessible depuis le reseau du garage.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6" data-testid="qr-display-screen">
      <div className="text-center space-y-6 max-w-md w-full">
        {/* Time */}
        <div className="text-6xl font-mono font-bold text-white tabular-nums tracking-tight" data-testid="qr-display-time">
          {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
        <p className="text-slate-400 text-sm">
          {currentTime.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

        {/* QR Code */}
        <div className="relative mx-auto w-72 h-72 bg-white rounded-3xl p-4 shadow-2xl shadow-blue-500/10">
          {qrValue ? (
            <QRCodeSVG
              value={qrValue}
              size={256}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#0f172a"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <RefreshCw className="w-10 h-10 text-slate-300 animate-spin" />
            </div>
          )}

          {/* Countdown ring */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-slate-900 border-2 border-slate-700 rounded-full px-4 py-1.5 flex items-center gap-2">
            <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-xs font-mono text-slate-400">{countdown}s</span>
          </div>
        </div>

        {/* Instructions */}
        <div className="space-y-2 pt-4">
          <div className="flex items-center justify-center gap-2 text-emerald-400">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-semibold">Pointage securise</span>
          </div>
          <p className="text-slate-500 text-xs">
            Scannez ce QR code depuis l'application Vortekio sur votre telephone pour pointer.
            <br />Le code change toutes les 30 secondes.
          </p>
        </div>

        {/* Vortekio branding */}
        <div className="flex items-center justify-center gap-2 pt-6">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <span className="text-white font-bold text-sm">V</span>
          </div>
          <span className="text-slate-600 text-sm font-medium">Vortekio</span>
        </div>
      </div>
    </div>
  );
};

export default TimeclockQRDisplay;
